<script setup>
import { ref, computed, watch, inject, onMounted, onBeforeUnmount } from 'vue'
import { useI18n } from 'vue-i18n'
import { createWalletWithClaim } from '@tokenbox/wallet-kit'
import GiftCard from './ui/GiftCard.vue'
import FlippableCard from './ui/FlippableCard.vue'
import CustomizeCard from './steps/CustomizeCard.vue'
import SecureYourGift from './steps/SecureYourGift.vue'
import TopUpAndShare from './steps/TopUpAndShare.vue'
import BaseModal from './ui/BaseModal.vue'

const { t: $t } = useI18n()

// Fixed currency - USDC only (no more currency selection)
const testnetOnly = import.meta.env.VITE_TESTNET_ONLY === 'true'
const FIXED_CURRENCY = {
  id: testnetOnly ? 'usdc-base-sepolia' : 'usdc-base',
  assetId: 'usdc',
  networkId: testnetOnly ? 'base_sepolia' : 'base',
  name: testnetOnly ? 'USDC (Base Sepolia)' : 'USDC (Base)',
  code: 'USDC',
  color: '#2775ca',
  type: 'erc20',
  decimals: 6,
  contractAddress: testnetOnly
    ? '0x036cbd53842c5426634e7929541ec2318f3dcf7e'
    : '0x833589fcd6edb6e08f4c7c32d4f71b54bda02913',
  networks: [
    {
      networkId: testnetOnly ? 'base_sepolia' : 'base',
      contractAddress: testnetOnly
        ? '0x036cbd53842c5426634e7929541ec2318f3dcf7e'
        : '0x833589fcd6edb6e08f4c7c32d4f71b54bda02913',
      decimals: 6
    }
  ]
}

const emit = defineEmits(['sharing-configured'])
const updateHeaderStep = inject('updateHeaderStep')
const registerNavigation = inject('registerNavigation', null)

const currentStep = ref(1)
const totalSteps = 3
const sharingStepRef = ref(null)
const customizeCardRef = ref(null)
const secureYourGiftRef = ref(null)
const topUpShareRef = ref(null)

// Reset confirmation modal
const showResetConfirmation = ref(false)

// Live card state (reactive for instant updates)
const liveBgStyle = ref(0)
const liveMessage = ref('Enjoy crypto!')

// Computed properties for card display
const currentCardCurrency = computed(() => {
  return FIXED_CURRENCY
})

const currentCardBalance = computed(() => {
  // On step 1 (customize), show selected amount (integer only)
  if (currentStep.value === 1 && stepperData.value.amount) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(stepperData.value.amount).replace('$', '')
  }

  // On step 2 (secure), show selected amount (integer only)
  if (currentStep.value === 2 && stepperData.value.amount) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(stepperData.value.amount).replace('$', '')
  }

  // On step 3 (topup), show only real balance (no expected amount fallback)
  if (currentStep.value === 3) {
    const realBalance = stepperData.value.deposit.balance

    // Show real balance if available, otherwise show 0
    if (realBalance && parseFloat(realBalance) > 0) {
      return realBalance
    }

    return '0'
  }

  return '0'
})

const cardMode = computed(() => {
  return currentStep.value >= 2 ? 'active' : 'customize'
})

// Stepper data state
const stepperData = ref({
  amount: null, // USD amount selected by user (null = not set yet)
  currency: FIXED_CURRENCY, // Always USDC (hardcoded)
  giftStyle: {
    bgStyle: 0,
    message: 'Enjoy crypto!',
    styleName: 'Classic'
  },
  wallet: {
    address: null,
    claimUrl: null,
    emojiPin: null,
    mnemonic: null,
    generated: false
  },
  deposit: {
    vault: null,
    qrCodeDataUrl: '',
    depositAddress: null,
    depositComplete: false,
    vaultContract: null,
    signerWallet: null,
    balance: null
  },
  sharing: {
    message: '',
    customUrl: '',
    cardDesign: 'default',
    emailRecipients: []
  }
})

const steps = computed(() => [
  {
    number: 1,
    title: $t('giftBoxStepper.steps.customizeGift.title', 'Customize Gift'),
    description: $t('giftBoxStepper.steps.customizeGift.description', 'Choose style and message'),
    completed: true // Customization is always optional, any choice is valid
  },
  {
    number: 2,
    title: $t('steps.secureYourGift', 'Secure Your Gift'),
    description: $t('steps.secureYourGiftDescription', 'Save claim instructions'),
    completed: !!stepperData.value.wallet.generated
  },
  {
    number: 3,
    title: $t('giftBoxStepper.steps.topUp.title', 'Top Up & Share'),
    description: $t('giftBoxStepper.steps.topUp.description', 'Fund and share your gift'),
    completed: !!stepperData.value.deposit.vault
  }
])

const canProceedToNextStep = computed(() => {
  switch (currentStep.value) {
    case 1:
      // Can always proceed from customize step (customization is optional)
      return true
    case 2:
      // Can proceed from secure step only if wallet is generated
      return !!stepperData.value.wallet.generated
    case 3:
      return true // Always allow proceeding from final step
    default:
      return false
  }
})

const showNavigationButtons = computed(() => {
  return currentStep.value > 1 // Show navigation for all steps except first
})

// Navigation methods
const nextStep = async () => {
  if (currentStep.value < totalSteps && canProceedToNextStep.value) {
    if (currentStep.value === 3 && sharingStepRef.value) {
      // For final step, trigger the handleFinish method
      sharingStepRef.value.handleFinish()
      return
    }

    // Generate wallet when moving from step 1 (customize) to step 2 (secure)
    if (currentStep.value === 1) {
      await generateWalletForGift()
    }

    currentStep.value++
  }
}

// Handler for SecureYourGift step continue event
const handleSecureStepContinue = () => {
  console.log('✅ [GiftBoxWizard] Secure step completed, proceeding to step 3')
  currentStep.value = 3
}

const prevStep = () => {
  if (currentStep.value > 1) {
    // If wallet is already generated and we're trying to go back from step 2 (secure) to step 1 (customize),
    // show confirmation modal (going back will create a new wallet)
    // Allow going back from step 3 (topup) to step 2 (secure) - user may want to view claim instructions again
    if (stepperData.value.wallet.generated && currentStep.value === 2) {
      showResetConfirmation.value = true
      return
    }

    currentStep.value--
  }
}

// Confirm reset and go back
const confirmReset = () => {
  showResetConfirmation.value = false

  // Reset all data
  stepperData.value = {
    amount: null,
    currency: FIXED_CURRENCY,
    giftStyle: {
      bgStyle: 0,
      message: 'Enjoy crypto!',
      styleName: 'Classic'
    },
    wallet: {
      address: null,
      claimUrl: null,
      emojiPin: null,
      mnemonic: null,
      generated: false
    },
    deposit: {
      vault: null,
      qrCodeDataUrl: '',
      depositAddress: null,
      depositComplete: false,
      vaultContract: null,
      signerWallet: null,
      balance: null
    },
    sharing: {
      message: '',
      customUrl: '',
      cardDesign: 'default',
      emailRecipients: []
    }
  }

  // Go back to step 1
  currentStep.value = 1
}

// Cancel reset
const cancelReset = () => {
  showResetConfirmation.value = false
}

const handleStartOver = () => {
  // Reset to step 1
  currentStep.value = 1
  // Clear all data
  stepperData.value = {
    amount: null,
    currency: FIXED_CURRENCY,
    giftStyle: {
      bgStyle: 0,
      message: 'Enjoy crypto!',
      styleName: 'Classic'
    },
    wallet: {
      address: null,
      claimUrl: null,
      emojiPin: null,
      mnemonic: null,
      generated: false
    },
    deposit: {
      vault: null,
      qrCodeDataUrl: '',
      address: null,
      depositComplete: false,
      balance: null
    },
    sharing: {
      message: '',
      customUrl: '',
      cardDesign: 'default',
      emailRecipients: []
    }
  }
}

const goToStep = (stepNumber) => {
  if (stepNumber <= currentStep.value || canProceedToStepDirectly(stepNumber)) {
    currentStep.value = stepNumber
  }
}

const canProceedToStepDirectly = (stepNumber) => {
  // Allow going to next step only if current is completed
  for (let i = 1; i < stepNumber; i++) {
    if (!steps.value[i - 1].completed) {
      return false
    }
  }
  return true
}

// Step event handlers
const handleAmountChanged = (data) => {
  console.log('💰 [GiftBoxWizard] Amount changed:', data)
  stepperData.value.amount = data.amountUsd
}

const handleAmountSelected = (data) => {
  console.log('💰 [GiftBoxWizard] Amount selected:', data)
  stepperData.value.amount = data.amountUsd
  nextStep()
}

const handleStyleSelected = async (styleData) => {
  console.log('🎨 [GiftBoxStepper] Style selected:', styleData)

  // Update live state for instant card preview
  if (styleData.bgStyle !== undefined) {
    liveBgStyle.value = styleData.bgStyle
  }
  if (styleData.message !== undefined) {
    liveMessage.value = styleData.message
  }

  // Save style data
  stepperData.value.giftStyle = {
    bgStyle: styleData.bgStyle,
    message: styleData.message || '',
    styleName: styleData.styleName || 'Classic'
  }

  // Wallet generation will happen when moving to step 3
  console.log('💾 [GiftBoxWizard] Gift style saved, wallet will be generated on step 3')
}

// Handle live style changes (called from controls)
const handleLiveStyleChange = (styleData) => {
  if (styleData.bgStyle !== undefined) {
    liveBgStyle.value = styleData.bgStyle
  }
  if (styleData.message !== undefined) {
    liveMessage.value = styleData.message
  }
}

const generateWalletForGift = async () => {
  // Skip if wallet already generated
  if (stepperData.value.wallet.generated) {
    console.log('✅ [GiftBoxWizard] Wallet already generated, skipping')
    return
  }

  try {
    // Generate wallet with claim URL using SDK
    console.log('⏳ [GiftBoxWizard] Generating wallet with claim URL...')

    const result = await createWalletWithClaim({
      chain: FIXED_CURRENCY.networkId,
      metadata: {
        bgStyle: stepperData.value.giftStyle.bgStyle,
        privateMessage: stepperData.value.giftStyle.message || '',
        amount: stepperData.value.amount // Store amount in metadata
      },
      baseUrl: window.location.origin,
      assetId: FIXED_CURRENCY.assetId,
    })

    console.log('✅ [GiftBoxWizard] Wallet generated:', {
      address: result.wallet.address,
      claimUrl: result.claimUrl,
      emojiPin: result.emojiPin,
      amount: stepperData.value.amount
    })

    // Save wallet data
    stepperData.value.wallet = {
      address: result.wallet.address,
      claimUrl: result.claimUrl,
      emojiPin: result.emojiPin,
      networkId: FIXED_CURRENCY.networkId,
      mnemonic: result.wallet.mnemonic || null,
      generated: true
    }

    // Use EOA wallet address directly for deposits
    stepperData.value.deposit.depositAddress = result.wallet.address
    stepperData.value.deposit.signerWallet = result.wallet.address

    console.log('📦 Deposit address set to EOA wallet:', result.wallet.address)
  } catch (error) {
    console.error('❌ [GiftBoxWizard] Failed to generate wallet:', error)
    // TODO: Show error notification
  }
}

const handleVaultCreated = (vaultData) => {
  stepperData.value.deposit.vault = vaultData
  stepperData.value.deposit.depositAddress = vaultData.deposit_address
  // Don't auto-advance, let user control when to proceed
}

const handleSharingConfigured = (sharingConfig) => {
  stepperData.value.sharing = { ...stepperData.value.sharing, ...sharingConfig }
  // If vault is created in final step, complete the process
  if (sharingConfig.vault) {
    stepperData.value.deposit.vault = sharingConfig.vault
    stepperData.value.deposit.depositAddress = sharingConfig.vault.deposit_address
  }

  // Final completion - emit all data
  const completeData = {
    ...stepperData.value,
    ...sharingConfig
  }
  console.log('Gift Box completed with config:', completeData)
  emit('sharing-configured', completeData)
}

const handleWalletLocked = () => {
  console.log('🔒 [GiftBoxWizard] Wallet locked event received.')

  // Clear sensitive data from stepper context
  // Note: Wallet component manages its own internal state
  stepperData.value.wallet.mnemonic = null
  stepperData.value.wallet.emojiPin = null
}

const handleWalletUnlocked = (data) => {
  console.log('🔓 [GiftBoxWizard] Wallet unlocked event received.')

  // Store sensitive data in stepper context for later use
  // Note: Wallet component manages its own internal state
  stepperData.value.wallet.mnemonic = data.mnemonic
  stepperData.value.wallet.emojiPin = data.emojiPin

  // Store metadata if needed
  if (data.metadata) {
    stepperData.value.wallet.networkId = data.metadata.chain || FIXED_CURRENCY.networkId
  }
}

const handleBalanceUpdated = (balance) => {
  console.log('💰 [GiftBoxWizard] Balance updated:', balance)
  stepperData.value.deposit.balance = balance
}

const handleResetWallet = () => {
  console.log('🔄 [GiftBoxWizard] Resetting wallet data')

  // Clear wallet data only (keep amount and gift style)
  stepperData.value.wallet = {
    address: null,
    claimUrl: null,
    emojiPin: null,
    mnemonic: null,
    generated: false
  }

  // Clear deposit data
  stepperData.value.deposit = {
    vault: null,
    qrCodeDataUrl: '',
    depositAddress: null,
    depositComplete: false,
    vaultContract: null,
    signerWallet: null,
    balance: null
  }

  // Go back to step 1 (customize)
  currentStep.value = 1
}

// Card menu handlers
const handleRefreshBalance = () => {
  console.log('🔄 [GiftBoxStepper] Refresh balance requested from card menu')
  // Delegate to TopUpAndShare component if on step 3
  if (currentStep.value === 3 && topUpShareRef.value) {
    topUpShareRef.value.refreshBalance?.()
  }
}

// Warn user before leaving/reloading page if wallet is generated
const handleBeforeUnload = (event) => {
  if (stepperData.value.wallet.generated) {
    // Modern browsers ignore custom messages, but still show a generic warning
    event.preventDefault()
    event.returnValue = ''
    return ''
  }
}

// Register navigation functions with parent
onMounted(() => {
  if (registerNavigation) {
    registerNavigation({
      goBack: prevStep
    })
  }

  // Add beforeunload listener
  window.addEventListener('beforeunload', handleBeforeUnload)
})

// Clean up event listener
onBeforeUnmount(() => {
  window.removeEventListener('beforeunload', handleBeforeUnload)
})

// Watch currentStep and update header
watch(currentStep, (newStep) => {
  if (updateHeaderStep) {
    updateHeaderStep(newStep)
  }
}, { immediate: true })

</script>

<template>
  <div class="flex flex-col h-full gap-2">
    <!-- Flippable Gift Card -->
    <div class="flex justify-center py-4 px-2">
      <FlippableCard :flipped="currentStep === 2">
        <!-- Front: Gift Card -->
        <template #front>
          <GiftCard
            :mode="cardMode"
            :balance="currentCardBalance"
            :currency="currentCardCurrency"
            :bg-style="liveBgStyle"
            v-model:message="liveMessage"
            :loading="false"
            :wallet-address="stepperData.wallet.address"
            :network-id="currentCardCurrency?.networkId"
            :mnemonic="stepperData.wallet.mnemonic"
            :claim-url="stepperData.wallet.claimUrl"
            :emoji-pin="stepperData.wallet.emojiPin"
            :current-step="currentStep"
            @refresh="handleRefreshBalance"
          />
        </template>

        <!-- Back: Gift Card Details -->
        <template #back>
          <SecureYourGift
            :claim-url="stepperData.wallet.claimUrl || ''"
            :emoji-pin="stepperData.wallet.emojiPin || ''"
            :wallet-address="stepperData.wallet.address || ''"
            :bg-style="liveBgStyle"
            :is-card-back="true"
          />
        </template>
      </FlippableCard>
    </div>

    <!-- Step Controls -->
    <div class="flex-1 relative">
      <!-- Step 1: Customize Card -->
      <CustomizeCard
        v-if="currentStep === 1"
        ref="customizeCardRef"
        :selected-currency="FIXED_CURRENCY"
        :wallet="stepperData.wallet"
        :initial-bg-style="liveBgStyle"
        :initial-message="liveMessage"
        @style-selected="handleStyleSelected"
        @live-change="handleLiveStyleChange"
        @next-step="nextStep"
      />

      <!-- Step 2: Secure Your Gift (External Content) -->
      <SecureYourGift
        v-if="currentStep === 2"
        ref="secureYourGiftRef"
        :claim-url="stepperData.wallet.claimUrl || ''"
        :emoji-pin="stepperData.wallet.emojiPin || ''"
        :wallet-address="stepperData.wallet.address || ''"
        :bg-style="liveBgStyle"
        :is-card-back="false"
        @continue="handleSecureStepContinue"
      />

      <!-- Step 3: Top Up & Share -->
      <TopUpAndShare
        v-if="currentStep === 3"
        ref="topUpShareRef"
        :selected-currency="FIXED_CURRENCY"
        :wallet="stepperData.wallet"
        :initial-bg-style="liveBgStyle"
        :initial-message="liveMessage"
        @wallet-locked="handleWalletLocked"
        @wallet-unlocked="handleWalletUnlocked"
        @balance-updated="handleBalanceUpdated"
        @reset-wallet="handleResetWallet"
      />
    </div>

    <!-- Footer -->
    <div class="text-center py-3">
      <span class="text-xs text-vault-text-secondary/60">
        Powered by <span class="font-medium">MyCryptoBox</span>
      </span>
    </div>

    <!-- Reset Confirmation Modal -->
    <BaseModal
      :open="showResetConfirmation"
      :close-on-backdrop="false"
      :close-on-esc="false"
      :show-close-button="false"
      max-width="400px"
      @close="cancelReset"
    >
      <div class="text-center px-2 py-2">
        <div class="text-4xl mb-4">⚠️</div>
        <h3 class="text-xl font-bold mb-3 text-vault-text-primary">
          {{ $t('giftBoxWizard.resetModal.title', 'Reset Gift Box?') }}
        </h3>
        <p class="text-sm text-vault-text-secondary mb-6 leading-relaxed">
          {{ $t('giftBoxWizard.resetModal.description', 'Going back will create a new wallet and reset your current gift box. If you haven\'t saved the claim URL and emoji PIN, access to this gift box will be lost. This action cannot be undone.') }}
        </p>
        <div class="flex gap-3">
          <button class="btn-secondary flex-1" @click="cancelReset">
            {{ $t('common.cancel', 'Cancel') }}
          </button>
          <button class="btn-danger flex-1" @click="confirmReset">
            {{ $t('giftBoxWizard.resetModal.confirm', 'Reset') }}
          </button>
        </div>
      </div>
    </BaseModal>
  </div>
</template>

<style scoped>
/* Styles removed - clean component structure */
</style>
